/**
 * The configuration panels (docs/ux/registry.md): Doctor, On the report,
 * Report types and overlays, Profile, Workspace, and the change history.
 * Each holds its own draft, saves one patch with the version read, and
 * shows the server's answer inline. The signature preview shows how the
 * block prints; the overlay table points at the templates registry.
 */

import { ALL_REPORT_TYPES, type ReportTypeKind } from '@beam/core/status';
import { cn } from 'cn';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import type { ClinicRecord, DictationMode, DoctorChange, DoctorPatch, DoctorRecord, DoctorView } from '@/app/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { dateTime, initials, longDate, pluralise } from '@/lib/format';
import { ConfigPanel, Field, ReadGrid, ReadPair, type PanelSaveState } from './ConfigPanel';
import { Link } from 'react-router';
import { DICTATION_DETAIL, DICTATION_WORD, NOT_SET, TITLES, TYPES, changeSentence, longType, shortType } from './describe';
import { useTemplatesIndex } from './templates/useTemplates';
import { UploadField } from './UploadField';
import { photoUrl, signatureUrl } from './useRegistry';

export type PanelId = 'doctor' | 'report' | 'templates' | 'profile' | 'workspace';

/** What every panel receives from the page. */
export type PanelProps = {
  doctor: DoctorRecord;
  editing: boolean;
  canEdit: boolean;
  editable: boolean;
  offline: boolean;
  state: PanelSaveState;
  onEdit: () => void;
  /** Cancel with the draft's dirtiness, so the page can ask before discarding. */
  onCancel: (dirty: boolean) => void;
  onReload: () => void;
  /** Save one patch; resolves true when it landed. */
  onSave: (patch: DoctorPatch) => Promise<boolean>;
  onLanded: (view: DoctorView) => void;
  /** The field a Fix link asked for; focused when the editor opens. */
  focusField: string | null;
  /** The page guards leaving the route while a draft differs from the saved values. */
  onDirty: (dirty: boolean) => void;
  phone: boolean;
};

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
const orNull = (s: string): string | null => (s.trim() ? s.trim() : null);

function useFocusField(editing: boolean, focusField: string | null, id: string) {
  useEffect(() => {
    if (!editing || !focusField) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`${id}-${focusField}`);
      const target = el?.matches('input, textarea, button, select, [tabindex]') ? el : el?.querySelector<HTMLElement>('button, input, textarea');
      target?.focus();
    });
  }, [editing, focusField, id]);
}

function useReportDirty(dirty: boolean, onDirty: (d: boolean) => void) {
  useEffect(() => { onDirty(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
}

/* Doctor: who the doctor is. */
export function DoctorPanel({ doctor: d, clinics, ...p }: PanelProps & { clinics: ClinicRecord[] }) {
  const id = useId();
  const read = { name: d.name, title: d.title ?? 'Dr', specialty: d.specialty, specialtyGroup: d.specialtyGroup ?? '', clinics: d.clinics, ahpra: d.credentials.ahpra ?? '', apia: d.credentials.apia ?? '', qualifications: d.credentials.qualificationsLine ?? '' };
  const [draft, setDraft] = useState(read);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => { if (!p.editing) { setDraft(read); setErrors({}); } }, [p.editing, d.version]); // eslint-disable-line react-hooks/exhaustive-deps
  useFocusField(p.editing, p.focusField, id);
  const dirty = !same(draft, read);
  useReportDirty(dirty, p.onDirty);

  async function save() {
    const errs: Record<string, string> = {};
    if (!draft.name.trim()) errs.name = 'Enter the name as Beam writes it.';
    if (!draft.specialty.trim()) errs.specialty = 'Enter the specialty; the readiness check needs it.';
    if (draft.ahpra.trim() && !/^MED\d{10}$/.test(draft.ahpra.trim())) errs.ahpra = 'An AHPRA number is MED followed by ten digits.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const patch: DoctorPatch = {};
    if (draft.name !== read.name) patch.name = draft.name.trim();
    if (draft.title !== read.title) patch.title = draft.title;
    if (draft.specialty !== read.specialty) patch.specialty = draft.specialty.trim();
    if (draft.specialtyGroup !== read.specialtyGroup) patch.specialtyGroup = orNull(draft.specialtyGroup);
    if (!same(draft.clinics, read.clinics)) patch.clinics = draft.clinics;
    if (draft.ahpra !== read.ahpra || draft.apia !== read.apia || draft.qualifications !== read.qualifications) patch.credentials = { ahpra: orNull(draft.ahpra), apia: orNull(draft.apia), qualificationsLine: orNull(draft.qualifications) };
    await p.onSave(patch);
  }

  const clinicName = (slug: string) => clinics.find((c) => c.id === slug)?.name ?? slug;
  return (
    <ConfigPanel
      id="panel-doctor" eyebrow="Doctor" editing={p.editing} canEdit={p.canEdit} editable={p.editable} offline={p.offline} dirty={dirty} state={p.state}
      onEdit={p.onEdit} onCancel={() => p.onCancel(dirty)} onSave={save} onReload={p.onReload}
      read={(
        <ReadGrid columns={p.phone ? 1 : 2}>
          <ReadPair label="Name" value={d.name} />
          <ReadPair label="Title" value={d.title} />
          <ReadPair label="Specialty" value={d.specialty} />
          <ReadPair label="Specialty group" value={d.specialtyGroup} />
          <ReadPair label="Clinics" value={d.clinics.length > 0 ? d.clinics.map(clinicName).join(', ') : null} span />
          <ReadPair label="AHPRA" value={d.credentials.ahpra ? <span className="tabular">{d.credentials.ahpra}</span> : null} />
          <ReadPair label="APIA" value={d.credentials.apia} />
          <ReadPair label="Qualifications" value={d.credentials.qualificationsLine} span />
        </ReadGrid>
      )}
      form={(
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Name" htmlFor={`${id}-name`} error={errors.name} help="As Beam writes it, with the title.">
            <Input id={`${id}-name`} data-autofocus value={draft.name} autoComplete="off" maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-9" aria-invalid={errors.name ? true : undefined} />
          </Field>
          <Field label="Title" htmlFor={`${id}-title`}>
            <Select value={draft.title} onValueChange={(v) => setDraft({ ...draft, title: v })}>
              <SelectTrigger id={`${id}-title`} className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Specialty" htmlFor={`${id}-specialty`} error={errors.specialty} help="Settles the Specialty check.">
            <Input id={`${id}-specialty`} value={draft.specialty} list={`${id}-specialties`} autoComplete="off" maxLength={120} onChange={(e) => setDraft({ ...draft, specialty: e.target.value })} className="h-9" aria-invalid={errors.specialty ? true : undefined} />
          </Field>
          <Field label="Specialty group" htmlFor={`${id}-group`} optional>
            <Input id={`${id}-group`} value={draft.specialtyGroup} autoComplete="off" maxLength={120} onChange={(e) => setDraft({ ...draft, specialtyGroup: e.target.value })} className="h-9" />
          </Field>
          <fieldset className="flex flex-col gap-1 md:col-span-2">
            <legend className="text-sm font-medium">Clinics <span className="font-normal text-beam-text-muted">(optional)</span></legend>
            <div className="flex flex-wrap gap-4 pt-1">
              {clinics.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={draft.clinics.includes(c.id)} onCheckedChange={(v) => setDraft({ ...draft, clinics: v ? [...draft.clinics, c.id] : draft.clinics.filter((x) => x !== c.id) })} />
                  {c.name}
                </label>
              ))}
            </div>
          </fieldset>
          <Field label="AHPRA" htmlFor={`${id}-ahpra`} optional error={errors.ahpra} help="MED followed by ten digits.">
            <Input id={`${id}-ahpra`} value={draft.ahpra} autoComplete="off" maxLength={40} inputMode="text" onChange={(e) => setDraft({ ...draft, ahpra: e.target.value })} onBlur={() => { if (draft.ahpra.trim() && !/^MED\d{10}$/.test(draft.ahpra.trim())) setErrors({ ...errors, ahpra: 'An AHPRA number is MED followed by ten digits.' }); else { const { ahpra: _a, ...rest } = errors; setErrors(rest); } }} className="tabular h-9" aria-invalid={errors.ahpra ? true : undefined} />
          </Field>
          <Field label="APIA" htmlFor={`${id}-apia`} optional help="The format is Beam's to confirm.">
            <Input id={`${id}-apia`} value={draft.apia} autoComplete="off" maxLength={40} onChange={(e) => setDraft({ ...draft, apia: e.target.value })} className="h-9" />
          </Field>
          <Field label="Qualifications" htmlFor={`${id}-qualifications`} optional help='For example "MBBS, FRACS (Orth)".' className="md:col-span-2">
            <Input id={`${id}-qualifications`} value={draft.qualifications} autoComplete="off" maxLength={200} onChange={(e) => setDraft({ ...draft, qualifications: e.target.value })} className="h-9" />
          </Field>
          <datalist id={`${id}-specialties`}>{['Occupational medicine', 'Orthopaedic surgery', 'Psychiatry', 'Rehabilitation medicine', 'Neurology', 'General practice', 'Pain medicine'].map((s) => <option key={s} value={s} />)}</datalist>
        </div>
      )}
    />
  );
}

/* On the report: how the doctor appears under the signature. */
export function SignaturePreview({ doctor: d, blockText, displayName, credentialLine }: { doctor: DoctorRecord; blockText: string | null; displayName: string | null; credentialLine: string | null }) {
  const lines = (blockText ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  const fallback = [displayName, credentialLine].filter((l): l is string => Boolean(l && l.trim()));
  return (
    <figure data-slot="signature-preview" className="max-w-[320px] rounded-lg bg-beam-cream-deep p-4">
      <figcaption className="eyebrow mb-2 text-beam-text-muted">Preview as it prints</figcaption>
      {d.signature.assetKey ? <img src={signatureUrl(d.slug, d.signature.version)} alt={`Signature image for ${d.name}`} className="mb-2 max-h-16" /> : <p className="mb-2 text-[13px] text-beam-text-muted">[no signature image]</p>}
      {(lines.length > 0 ? lines : fallback.length > 0 ? fallback : [displayName ?? d.name]).map((l, i) => <p key={i} className="text-sm leading-snug">{l}</p>)}
      {lines.length === 0 && !credentialLine ? <p className="text-[13px] text-beam-text-muted">(credential line not set)</p> : null}
      <p className="mt-2 text-xs text-beam-text-muted">Layout only; the report uses the template's typeface.</p>
    </figure>
  );
}

export function OnTheReportPanel({ doctor: d, ...p }: PanelProps) {
  const id = useId();
  const read = { displayName: d.displayName ?? '', credentialLine: d.display.credentialLine ?? '', block: d.signature.blockText ?? '' };
  const [draft, setDraft] = useState(read);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (p.editing) return;
    setDraft(read); setErrors({});
  }, [p.editing, d.version]); // eslint-disable-line react-hooks/exhaustive-deps
  // Opening the editor prefills what is empty so the admin's cost is one glance (docs/ux/registry.md, section 7).
  useEffect(() => {
    if (!p.editing) return;
    setDraft((cur) => ({
      displayName: cur.displayName || d.name,
      credentialLine: cur.credentialLine || (d.credentials.qualificationsLine ?? ''),
      block: cur.block || [d.displayName ?? d.name, d.display.credentialLine ?? d.credentials.qualificationsLine ?? ''].filter(Boolean).join('\n'),
    }));
  }, [p.editing]); // eslint-disable-line react-hooks/exhaustive-deps
  useFocusField(p.editing, p.focusField, id);
  const dirty = !same(draft, read);
  useReportDirty(dirty, p.onDirty);

  async function save() {
    const errs: Record<string, string> = {};
    if (!draft.displayName.trim()) errs.displayName = 'Enter the name as it prints on the report.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const patch: DoctorPatch = {};
    if (draft.displayName !== read.displayName) patch.displayName = orNull(draft.displayName);
    if (draft.credentialLine !== read.credentialLine) patch.credentialLine = orNull(draft.credentialLine);
    if (draft.block !== read.block) patch.signatureBlockText = orNull(draft.block);
    await p.onSave(patch);
  }

  const signatureCurrent = d.signature.assetKey ? { preview: signatureUrl(d.slug, d.signature.version), name: `Signature image, version ${d.signature.version}`, addedAt: addedAt(d, `signature_attached:v${d.signature.version}`) ?? undefined, addedBy: addedBy(d, `signature_attached:v${d.signature.version}`) ?? undefined } : null;
  return (
    <ConfigPanel
      id="panel-report" eyebrow="On the report" editing={p.editing} canEdit={p.canEdit} editable={p.editable} offline={p.offline} dirty={dirty} state={p.state}
      onEdit={p.onEdit} onCancel={() => p.onCancel(dirty)} onSave={save} onReload={p.onReload}
      read={(
        <div className="flex flex-col gap-4">
          <ReadGrid columns={p.phone ? 1 : 2}>
            <ReadPair label="Display name" value={d.displayName} />
            <ReadPair label="Credential line" value={d.display.credentialLine} />
            <ReadPair label="Signature image" value={d.signature.assetKey ? <img src={signatureUrl(d.slug, d.signature.version)} alt={`Signature image for ${d.name}`} className="max-h-12" /> : <span className="text-beam-text-muted">No image</span>} />
            <ReadPair label="Signature block" value={d.signature.blockText ? <span className="whitespace-pre-line">{d.signature.blockText}</span> : null} />
          </ReadGrid>
          <SignaturePreview doctor={d} blockText={d.signature.blockText} displayName={d.displayName} credentialLine={d.display.credentialLine} />
        </div>
      )}
      form={(
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Display name" htmlFor={`${id}-displayName`} error={errors.displayName} help="As it prints above the credential line.">
              <Input id={`${id}-displayName`} data-autofocus value={draft.displayName} autoComplete="off" maxLength={160} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} className="h-9" aria-invalid={errors.displayName ? true : undefined} />
            </Field>
            <Field label="Credential line" htmlFor={`${id}-credentialLine`} help='Printed under the display name, for example "MBBS, FRACS (Orth)". Settles the check with the display name.'>
              <Input id={`${id}-credentialLine`} value={draft.credentialLine} autoComplete="off" maxLength={200} onChange={(e) => setDraft({ ...draft, credentialLine: e.target.value })} className="h-9" />
            </Field>
          </div>
          <div className="flex flex-col gap-1" id={`${id}-signature`}>
            <span className="text-sm font-medium">Signature image</span>
            <UploadField slug={d.slug} target={{ kind: 'signature' }} label="Signature image" accept={{ mime: ['image/png'], extensions: ['.png'], words: 'a PNG image' }} maxBytes={5 * 1024 * 1024} offline={p.offline} current={signatureCurrent} onLanded={(view) => p.onLanded(view)} />
          </div>
          <Field label="Signature block" htmlFor={`${id}-block`} help="Printed beside the signature image, one line per line. The readiness check needs the image and the block.">
            <Textarea id={`${id}-block`} rows={3} maxLength={500} value={draft.block} onChange={(e) => setDraft({ ...draft, block: e.target.value })} className="max-w-[480px]" />
          </Field>
          <SignaturePreview doctor={d} blockText={draft.block} displayName={draft.displayName} credentialLine={draft.credentialLine} />
        </div>
      )}
    />
  );
}

function addedAt(d: DoctorRecord, code: string): string | null {
  const c = d.audit.find((x) => x.change === code);
  return c ? longDate(c.at) : null;
}
function addedBy(d: DoctorRecord, code: string): string | null {
  const c = d.audit.find((x) => x.change === code);
  return c ? c.byName ?? c.by : null;
}

/* Report types and overlays (docs/ux/templates.md): which types the doctor offers, the base each generates from, and the doctor's overlay. */
export function TemplatesPanel({ doctor: d, ...p }: PanelProps) {
  const id = useId();
  const [offered, setOffered] = useState<ReportTypeKind[]>(d.reportTypes);
  const [error, setError] = useState<string | null>(null);
  const index = useTemplatesIndex();
  useEffect(() => { if (!p.editing) { setOffered(d.reportTypes); setError(null); } }, [p.editing, d.version]); // eslint-disable-line react-hooks/exhaustive-deps
  useFocusField(p.editing, p.focusField, id);
  const dirty = !same([...offered].sort(), [...d.reportTypes].sort());
  useReportDirty(dirty, p.onDirty);

  async function save() {
    if (offered.length === 0) { setError('A doctor offers at least one report type.'); return; }
    setError(null);
    await p.onSave({ reportTypes: ALL_REPORT_TYPES.filter((t) => offered.includes(t)) });
  }

  const rows = TYPES.map((t) => ({
    t,
    offered: (p.editing ? offered : d.reportTypes).includes(t),
    base: index.data?.bases.find((b) => b.head.id === t) ?? null,
    overlay: index.data?.overlays.find((o) => o.doctorSlug === d.slug && o.reportType === t) ?? null,
  }));
  const visible = p.phone ? rows.filter((r) => r.offered) : rows;
  return (
    <ConfigPanel
      id="panel-templates" eyebrow="Report types and overlays" editing={p.editing} canEdit={p.canEdit} editable={p.editable} offline={p.offline} dirty={dirty} state={{ ...p.state, error: error ?? p.state.error }}
      onEdit={p.onEdit} onCancel={() => p.onCancel(dirty)} onSave={save} onReload={p.onReload}
      description="Tick the types this doctor offers. Each type generates from its base under Templates; the doctor's overlay carries what differs."
      read={<OverlayTable rows={visible} doctor={d} editing={false} phone={p.phone} loading={index.isPending} unofferedCount={rows.length - visible.length} />}
      form={<OverlayTable rows={rows} doctor={d} editing offered={offered} onOffered={setOffered} phone={p.phone} loading={index.isPending} unofferedCount={0} idPrefix={id} />}
    />
  );
}

type OverlayRow = { t: ReportTypeKind; offered: boolean; base: { head: { liveVersion: number } } | null; overlay: { head: { liveVersion: number }; source: string; adds: number; removes: number; renames: number } | null };

function OverlayTable({ rows, doctor: d, editing, offered, onOffered, phone, loading, unofferedCount, idPrefix }: {
  rows: OverlayRow[]; doctor: DoctorRecord; editing: boolean; offered?: ReportTypeKind[]; onOffered?: (o: ReportTypeKind[]) => void; phone: boolean; loading: boolean; unofferedCount: number; idPrefix?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Table>
        <caption className="sr-only">Report types this doctor offers, their bases and overlays</caption>
        <TableHeader className="bg-beam-cream-deep">
          <TableRow className="hover:bg-beam-cream-deep">
            {editing ? <TableHead scope="col" className="eyebrow h-8 w-[72px] px-3 text-beam-text-soft">Offered</TableHead> : null}
            <TableHead scope="col" className="eyebrow h-8 px-3 text-beam-text-soft">Report type</TableHead>
            <TableHead scope="col" className="eyebrow h-8 w-[110px] px-3 text-beam-text-soft">Base</TableHead>
            <TableHead scope="col" className="eyebrow h-8 px-3 text-beam-text-soft">Overlay</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ t, offered: isOffered, base, overlay }) => (
            <TableRow key={t} data-slot="template-row" data-type={t} className={cn('border-beam-line align-top hover:bg-transparent', !isOffered && 'text-beam-text-muted')}>
              {editing ? (
                <TableCell className="px-3 py-3">
                  <Checkbox id={`${idPrefix}-offered-${t}`} data-autofocus={t === 'ime' ? true : undefined} checked={offered!.includes(t)} aria-label={`Offer ${longType(t)}`} onCheckedChange={(v) => onOffered!(v === true ? [...offered!, t] : offered!.filter((x) => x !== t))} />
                </TableCell>
              ) : null}
              <TableCell className="px-3 py-3 text-[13px] font-medium">{phone ? shortType(t) : longType(t)}</TableCell>
              <TableCell className="tabular px-3 py-3 text-[13px]" data-slot="base-version">{loading ? '' : base ? `Version ${base.head.liveVersion}` : 'No report'}</TableCell>
              <TableCell className="whitespace-normal px-3 py-3 text-[13px]" data-slot="overlay-status">
                {!isOffered ? 'Not offered' : !base ? 'Produces no generated report' : loading ? '' : (
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>{overlay ? `Overlay version ${overlay.head.liveVersion} (${overlay.source}): ${overlay.removes} left out, ${overlay.adds} added, ${overlay.renames} renamed` : 'Uses the base'}</span>
                    {!phone && !editing ? <Link to={`/registry/templates/${t}/doctors/${encodeURIComponent(d.slug)}`} className="text-[13px] underline-offset-2 hover:underline" aria-label={`${overlay ? 'Edit' : 'Create'} the ${shortType(t)} overlay for ${d.name}`}>{overlay ? 'Edit overlay' : 'Create overlay'}</Link> : null}
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {unofferedCount > 0 ? <p className="text-[13px] text-beam-text-muted">{pluralise(unofferedCount, 'type')} not offered.</p> : null}
    </div>
  );
}

/* Profile: the public face. */
export function ProfilePanel({ doctor: d, ...p }: PanelProps) {
  const id = useId();
  const read = { bio: d.display.bio ?? '' };
  const [draft, setDraft] = useState(read);
  useEffect(() => { if (!p.editing) setDraft(read); }, [p.editing, d.version]); // eslint-disable-line react-hooks/exhaustive-deps
  useFocusField(p.editing, p.focusField, id);
  const dirty = !same(draft, read);
  useReportDirty(dirty, p.onDirty);
  const photo = d.display.photoKey ? photoUrl(d.slug, d.display.photoVersion) : null;
  const avatar = (
    <Avatar className="size-16">
      {photo ? <AvatarImage src={photo} alt={d.displayName ?? d.name} /> : null}
      <AvatarFallback aria-hidden="true" className="bg-beam-cream-deep text-base">{initials(d.name.replace(/^(Dr|Professor|Mr|Ms)\s+/, ''))}</AvatarFallback>
    </Avatar>
  );
  return (
    <ConfigPanel
      id="panel-profile" eyebrow="Profile" editing={p.editing} canEdit={p.canEdit} editable={p.editable} offline={p.offline} dirty={dirty} state={p.state}
      onEdit={p.onEdit} onCancel={() => p.onCancel(dirty)} onSave={() => p.onSave({ bio: orNull(draft.bio) })} onReload={p.onReload}
      read={(
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex items-center gap-3">{avatar}<span className="text-sm text-beam-text-muted">{photo ? 'Photo on file' : 'No photo'}</span></div>
          <ReadGrid columns={1}><ReadPair label="Bio" value={d.display.bio ? <span className="whitespace-pre-line">{d.display.bio}</span> : null} /></ReadGrid>
        </div>
      )}
      form={(
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Photo <span className="font-normal text-beam-text-muted">(optional)</span></span>
            <div className="flex items-start gap-3">{avatar}<div className="min-w-0 flex-1"><UploadField slug={d.slug} target={{ kind: 'photo' }} label="Photo" accept={{ mime: ['image/png', 'image/jpeg'], extensions: ['.png', '.jpg', '.jpeg'], words: 'a JPEG or PNG image' }} maxBytes={5 * 1024 * 1024} offline={p.offline} compact current={photo ? { name: `Photo, version ${d.display.photoVersion}` } : null} onLanded={(view) => p.onLanded(view)} /></div></div>
          </div>
          <Field label="Bio" htmlFor={`${id}-bio`} optional help={draft.bio.length >= 500 ? `${Math.max(0, 600 - draft.bio.length)} characters left` : 'A few sentences, as Beam writes it on the site.'}>
            <Textarea id={`${id}-bio`} data-autofocus rows={4} maxLength={2000} value={draft.bio} onChange={(e) => setDraft({ bio: e.target.value })} />
          </Field>
        </div>
      )}
    />
  );
}

/* Workspace: the doctor's own dictation set-up; the one panel the phone edits. */
/** The standing preference's copy (D-0059 and its amendment): proofreading only underlines, so it is safe on; formatting changes text, so it is off. */
export const PROOFREADING_HELP = 'Underlines spelling and grammar in the workspace; nothing changes without acceptance. Automatic formatting is separate and off.';

export function WorkspaceFields({ id, mode, onMode, terms, onTerms, proofreading, onProofreading, phone }: { id: string; mode: DictationMode; onMode: (m: DictationMode) => void; terms: string; onTerms: (t: string) => void; proofreading: boolean; onProofreading: (on: boolean) => void; phone: boolean }) {
  const count = terms.split('\n').map((s) => s.trim()).filter(Boolean).length;
  return (
    <>
      <div className="flex flex-col gap-1">
        <span id={`${id}-mode-label`} className="text-sm font-medium">Dictation mode</span>
        <ToggleGroup type="single" value={mode} onValueChange={(v) => { if (v) onMode(v as DictationMode); }} aria-labelledby={`${id}-mode-label`} className="justify-start rounded-lg bg-beam-cream-deep p-1">
          {(['platform', 'bridge'] as DictationMode[]).map((m) => <ToggleGroupItem key={m} value={m} data-autofocus={m === 'platform' ? true : undefined} className={cn('px-3 text-sm data-[state=on]:bg-beam-paper data-[state=on]:text-foreground data-[state=on]:shadow-beam-1', phone ? 'h-11' : 'h-9')}>{DICTATION_WORD[m]}</ToggleGroupItem>)}
        </ToggleGroup>
        <p className="text-xs text-beam-text-muted">{DICTATION_DETAIL[mode]}.</p>
      </div>
      <Field label="Key terms" htmlFor={`${id}-terms`} optional help={`One term per line. ${pluralise(count, 'term')}. The workspace's proofreader also takes them as its dictionary.`}>
        <Textarea id={`${id}-terms`} rows={4} value={terms} onChange={(e) => onTerms(e.target.value)} className={cn('max-w-[480px]', phone && 'text-[16px]')} />
      </Field>
      <div className="flex flex-col gap-1">
        <label data-slot="proofreading-label" className={cn('flex items-center gap-3 text-sm font-medium', phone ? 'min-h-11' : 'min-h-9')}>
          <Switch checked={proofreading} onCheckedChange={onProofreading} aria-describedby={`${id}-proof-help`} data-slot="proofreading-switch" />
          <span>Proofreading {proofreading ? 'on' : 'off'}</span>
        </label>
        <p id={`${id}-proof-help`} className="text-xs text-beam-text-muted">{PROOFREADING_HELP}</p>
      </div>
    </>
  );
}

export function WorkspacePanel({ doctor: d, ...p }: PanelProps) {
  const id = useId();
  const read = { mode: d.workspace.dictationMode, terms: d.workspace.keyterms.join('\n'), proofreading: d.workspace.proofreading !== false };
  const [draft, setDraft] = useState(read);
  useEffect(() => { if (!p.editing) setDraft(read); }, [p.editing, d.version]); // eslint-disable-line react-hooks/exhaustive-deps
  useFocusField(p.editing, p.focusField, id);
  const dirty = !same(draft, read);
  useReportDirty(dirty, p.onDirty);
  const terms = useMemo(() => [...new Set(draft.terms.split('\n').map((s) => s.trim()).filter(Boolean))], [draft.terms]);
  return (
    <ConfigPanel
      id="panel-workspace" eyebrow="Workspace" editing={p.editing} canEdit={p.canEdit} editable offline={p.offline} dirty={dirty} state={p.state}
      onEdit={p.onEdit} onCancel={() => p.onCancel(dirty)} onSave={() => p.onSave({ ...(draft.mode !== read.mode ? { dictationMode: draft.mode } : {}), ...(draft.terms !== read.terms ? { keyterms: terms } : {}), ...(draft.proofreading !== read.proofreading ? { proofreading: draft.proofreading } : {}) })} onReload={p.onReload}
      read={(
        <ReadGrid columns={1}>
          <ReadPair label="Dictation mode" value={<><span data-slot="dictation-mode">{DICTATION_WORD[d.workspace.dictationMode]}</span> <span className="text-beam-text-muted">({DICTATION_DETAIL[d.workspace.dictationMode]})</span></>} />
          <ReadPair label="Key terms" value={d.workspace.keyterms.length > 0 ? d.workspace.keyterms.join(', ') : <span className="text-beam-text-muted">None yet</span>} />
          <ReadPair label="Proofreading" value={<><span data-slot="proofreading">{read.proofreading ? 'On' : 'Off'}</span> <span className="text-beam-text-muted">({read.proofreading ? 'underlines in the workspace; nothing changes without acceptance' : 'no underlines; the doctor can turn it on for one report from the workspace'})</span></>} />
        </ReadGrid>
      )}
      form={<WorkspaceFields id={id} mode={draft.mode} onMode={(m) => setDraft({ ...draft, mode: m })} terms={draft.terms} onTerms={(t) => setDraft({ ...draft, terms: t })} proofreading={draft.proofreading} onProofreading={(on) => setDraft({ ...draft, proofreading: on })} phone={p.phone} />}
    />
  );
}

/* Change history: the audit's face on the page. */
export function ChangeHistory({ audit, phone }: { audit: DoctorChange[]; phone: boolean }) {
  const [all, setAll] = useState(false);
  const rows = [...audit].reverse();
  const shown = all ? rows : rows.slice(0, 10);
  const body = (
    <>
      {rows.length === 0 ? <p className="text-sm text-beam-text-muted">No changes recorded yet.</p> : (
        <ol data-slot="change-history" className="divide-y divide-beam-line text-[13px]">
          {shown.map((c, i) => (
            <li key={`${c.at}-${i}`} className="flex flex-col gap-0.5 py-2 md:flex-row md:items-baseline md:gap-4">
              <span className="tabular shrink-0 text-beam-text-muted md:w-32">{dateTime(c.at)}</span>
              <span className="shrink-0 font-medium md:w-40">{c.byName ?? c.by}</span>
              <span>{changeSentence(c.change)}</span>
            </li>
          ))}
        </ol>
      )}
      {rows.length > 10 && !all ? <Button type="button" variant="link" className="h-auto p-0 text-[13px]" onClick={() => setAll(true)}>Show all {rows.length}</Button> : null}
    </>
  );
  if (phone) {
    return (
      <Collapsible data-slot="config-panel" data-panel="history" className="rounded-xl border border-beam-line bg-beam-paper p-4">
        <CollapsibleTrigger className="flex min-h-11 w-full items-center gap-1 text-left"><ChevronRightIcon aria-hidden="true" className="size-4 transition-transform duration-[140ms] [[data-state=open]>&]:rotate-90" /><span className="eyebrow text-beam-text-muted">Change history</span> <span className="tabular text-sm text-beam-text-muted">{rows.length}</span></CollapsibleTrigger>
        <CollapsibleContent className="pt-2">{body}</CollapsibleContent>
      </Collapsible>
    );
  }
  return (
    <section aria-labelledby="history-heading" data-slot="config-panel" data-panel="history" className="rounded-xl border border-beam-line bg-beam-paper p-4">
      <h2 id="history-heading" className="eyebrow text-beam-text-muted">Change history <span className="tabular ml-2 font-normal normal-case tracking-normal">{pluralise(rows.length, 'change')}</span></h2>
      <div className="mt-2">{body}</div>
    </section>
  );
}

export function NotSetWord(): ReactNode {
  return <span className="text-beam-text-muted">{NOT_SET}</span>;
}
